import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Briefcase, MapPin, Building2, Globe } from "lucide-react";
import { getTheme, fonts } from "../theme";
import Navbar from "../components/Navbar";
import SiteFooter from "../components/SiteFooter";
import { Button } from "../components/UI";
import api from "../api";

const BOARD_COLORS = {
  naukri: "#0B5CAB",
  linkedin: "#0A66C2",
  indeed: "#2164F3",
  foundit: "#6C5CE7",
  glassdoor: "#00A264"
};

export default function PublicJobPage({ onLogout, themeMode = "dark", setThemeMode }) {
  const theme = getTheme("job_seeker", themeMode);
  const { slug } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.getPublicJob(slug).then((res) => {
      if (!cancelled) setJob(res.job || res);
    }).catch((err) => {
      if (!cancelled) setError(err.message || "Job not found.");
    });
    return () => { cancelled = true; };
  }, [slug]);

  return (
    <div style={{ minHeight: "100vh", background: theme.bg, color: theme.text, fontFamily: fonts.body }}>
      <Navbar theme={theme} onLogout={onLogout} themeMode={themeMode} setThemeMode={setThemeMode} />
      <div className="container py-5" style={{ maxWidth: 760 }}>
        {error && <div className="alert alert-danger">{error}</div>}
        {!error && !job && <div style={{ color: theme.textMute }}>Loading job…</div>}
        {job && (
          <div className="card p-4 rounded-4 shadow-sm" style={{ background: theme.surface, border: `1px solid ${theme.line}`, color: theme.text }}>
            <div className="d-flex align-items-center gap-2 mb-2" style={{ color: theme.accent }}>
              <Briefcase size={18} />
              <span className="small fw-bold text-uppercase">Live job on WIBIL</span>
            </div>
            <h1 className="h3 fw-bold" style={{ fontFamily: fonts.display }}>{job.role}</h1>
            <div className="small d-flex flex-wrap gap-3 mb-3" style={{ color: theme.textMute }}>
              <span className="d-inline-flex align-items-center gap-1"><Building2 size={14} />{job.company_name || job.recruiter_company}</span>
              {(job.location || job.recruiter_location) && <span className="d-inline-flex align-items-center gap-1"><MapPin size={14} />{job.location || job.recruiter_location}</span>}
              {job.salary_range && <span>{job.salary_range}</span>}
              {job.experience && <span>{job.experience}</span>}
            </div>
            <div className="d-flex flex-wrap gap-2 mb-4">
              {(job.boardPosts || []).map((board) => (
                <span key={board.id} className="badge rounded-pill px-3 py-2" style={{ background: `${BOARD_COLORS[board.id] || theme.accent}18`, color: BOARD_COLORS[board.id] || theme.accent, border: `1px solid ${BOARD_COLORS[board.id] || theme.accent}44` }}>
                  <Globe size={11} className="me-1" />{board.label}
                </span>
              ))}
            </div>
            {job.description && <p style={{ whiteSpace: "pre-wrap" }}>{job.description}</p>}
            {job.skills && <p className="small"><strong>Skills:</strong> {job.skills}</p>}
            <div className="d-flex gap-2 mt-4">
              <Button theme={theme} onClick={async () => {
                let sessionUser = null;
                try {
                  const raw = sessionStorage.getItem("wibil_user") || localStorage.getItem("wibil_user");
                  sessionUser = raw && raw !== "undefined" ? JSON.parse(raw) : null;
                } catch (e) {}
                const role = String(sessionUser?.role || "").toLowerCase();
                if (sessionUser?.id && (role === "employee" || role === "job_seeker" || role === "jobseeker")) {
                  try {
                    await api.applyToJob({ jobId: job.id, employeeId: sessionUser.id });
                    navigate("/job-seeker", { replace: true });
                    return;
                  } catch (err) {}
                }
                navigate(`/apply-for-job?jobId=${encodeURIComponent(job.id)}`);
              }}>Apply on WIBIL</Button>
              <Link to="/login/job-seeker" className="btn btn-outline-secondary">Sign in to apply</Link>
            </div>
          </div>
        )}
      </div>
      <SiteFooter theme={theme} />
    </div>
  );
}
